<!--
    How to use comments :)

    title                   = String for the modal heading (Default = null)
    open/v-model            = Boolean for modal open state (Default = false)
    tableData               = Array of objects as passed into normal table (Required)
    columns                 = Array of string column keys or objects in format of b-table field (Not Required - Default b-table fields behaviour)
                                (can include custom bool for copying to clipboard i.e.
                                    {
                                        key: 'name',
                                        label: 'Name',
                                        canCopy: true
                                    }
                                )
    striped                 = Bool for b-table striped property (Default = true)
    small                   = Bool for b-table small property (Default = true)
    hover                   = Bool for b-table hover property (Default = true)
    textSize                = String for b-button size property (Default = "sm")
    modalSize               = String for b-modal size property (Default = "xl")
    selectable              = Bool for b-table to enable selection (Default = "false")
    selectMode              = String for b-table select-mode (Default = "multi")
    selectableKey           = String, the key that will be extracted from selected rows
    selectedKeys            = Array, if provided, the included keys will be selected in the table when the dialog opens
    unselectedKeys          = Array, if provided (and selectedKey is null!), the included keys will NOT be selected in the table when the dialog opens (all other rows will be selected)

    events:
    ok                      = emitted when the OK button is pressed, contains an array of selected keys when the dialog is selectable
    cancel                  = emitted when the Cancel button is pressed
    hidden                  = emitted when the dialog is hidden
    change                  = v-model event, emitted when the open prop changes
    -->


<template>
    <div>
        <validation-observer slim>
            <b-modal :size="modalSize"
                     centered
                     no-close-on-backdrop
                     no-close-on-esc
                     no-enforce-focus
                     v-model="currentOpen"
                     @cancel="cancelModal"
                     @ok="okModal"
                     @hidden="modalHidden">
                <template #modal-header>
                    <h5 class="modal-title">{{title}}</h5>
                </template>
                <template #default>
                    <b-table ref="table"
                             :striped="striped"
                             :small="small"
                             :hover="hover"
                             :fields="columns"
                             :items="tableData"
                             :selectable="selectable"
                             :select-mode="selectMode"
                             sticky-header
                             v-on:row-selected="onRowSelected">
                        <template #cell()="data">
                            <div>

                                <b-button variant="link" size="sm"
                                          v-if="canCopyToClipboard(data)"
                                          @click="copyToClipboard(data.value)"
                                          :ref="'copytext' + data.field.key + data.index"
                                          :id="'copytext' + data.field.key + data.index">
                                    {{data.value}}
                                    <i class="fas fa-copy"></i>
                                </b-button>
                                <b-tooltip v-if="canCopyToClipboard(data)" :target="'copytext' + data.field.key + data.index" triggers="hover" variant="info" solid="true" class="p-2">
                                    Copy to Clipboard 
                                </b-tooltip>

                                <b-button v-else variant="link" :size="textSize"
                                          class="btn-mimic-text"
                                          disabled>
                                    {{data.value}}
                                </b-button>
                            </div>
                        </template>

                    </b-table>
                </template>

                <template #modal-footer="{ ok, cancel }">
                    <b-button v-if="selectable" type="button" variant="info" @click="selectAll">Select All</b-button>
                    <b-button v-if="selectable" type="button" variant="info" @click="selectNone">Select None</b-button>
                    <b-button type="button" @click="cancel()">Cancel</b-button>
                    <b-button v-if="selectable" :disabled="!currentSelectedKeys || !currentSelectedKeys.length" type="button" variant="success" @click="ok()">OK</b-button>
                </template>
            </b-modal>
        </validation-observer>
    </div>
</template>

<script>
    import Vue from 'vue';
    import BootstrapVue from 'bootstrap-vue';


    Vue.use(BootstrapVue);



    export default {
        name: 'DataTableModal',
        model: {
            event: 'change',
            prop: 'open'
        },
        props: {
            title: {
                type: String,
                default: null
            },
            modalSize: {
                type: String,
                default: 'xl'
            },
            open: {
                type: Boolean,
                default: false
            },
            selectable: {
                type: Boolean,
                default: false
            },
            selectMode: {
                type: String,
                default: "multi"
            },
            selectableKey: {
                type: String,
                default: null
            },
            selectedKeys: {
                type: Array,
                default: null
            },
            unselectedKeys: {
                type: Array,
                default: null
            },
            tableData: {
                type: Array,
                required: true
            },
            columns: {
                type: Array,
                required: false,
                default: null,
            },
            striped: {
                type: Boolean,
                required: false,
                default: true
            },
            small: {
                type: Boolean,
                required: false,
                default: true
            },
            hover: {
                type: Boolean,
                required: false,
                default: true
            },
            textSize: {
                type: String,
                required: false,
                default: "sm"
            }
        },
        data: function () {
            return {
                currentOpen: this.open,
                currentSelectedKeys: []
            }
        },
        watch: {
            open: function () {
                this.currentOpen = this.open;
                if (this.currentOpen) {
                    this.currentSelectedKeys = this.selectedKeys ? this.selectedKeys : this.unselectedKeys ? this.tableData.filter(e => !this.unselectedKeys.includes(e[this.selectableKey])).map(e => e[this.selectableKey]) : [];
                    this.setInitialSelection();
                }
            },
        },
        methods: {
            cancelModal: function () {
                this.$emit('cancel');
            },
            okModal: function () {
                if (this.selectable) this.$emit('ok', this.currentSelectedKeys);
                else this.$emit('ok');
            },
            modalHidden: function () {
                this.$emit('change', this.currentOpen);
                this.$emit('hidden');
            },
            copyToClipboard: function (args) {
                navigator.clipboard.writeText(args);
                this.$bvToast.toast(args, {
                    title: 'Copied to Clipboard',
                    variant: 'success',
                    appendToast: true
                });
            },
            canCopyToClipboard(args) {
                if (args.value.toString().trim().length === 0) return false;
                if (!args.field.canCopy) return false;
                return args.field.canCopy;
            },
            selectAll() {
                this.$refs.table.selectAllRows();
            },
            selectNone() {
                this.$refs.table.clearSelected();
            },
            onRowSelected(items) {
                if (items && items.length) {
                    this.currentSelectedKeys = items.map(item => item[this.selectableKey]);
                } else {
                    this.currentSelectedKeys = [];
                }
            },
            setInitialSelection() {
                if (this.currentSelectedKeys && this.currentSelectedKeys.length) {
                    this.$nextTick(() => {
                        this.currentSelectedKeys.forEach(key => {
                            var index = this.tableData.findIndex(e => e[this.selectableKey] == key);
                            if (index >= 0) {
                                this.$refs.table.selectRow(index);
                            }
                        });
                    });
                }
            }
        },
        mounted: function () {
            if (this.currentOpen) this.setInitialSelection();
        }
    }
</script>

<style>
    .btn-mimic-text:disabled {
        color: black !important;
    }
</style>